import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaEye, FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import useFetch from "../../../components/shared/useFetch";
import BurialView from "./BurialView";
import dayjs from "dayjs";

const BurialRegisterChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [{ errorLogApi }] = useAtom(gbState);

  const url = `${process.env.REACT_APP_API_URL}/burial/`;
  const { data: burials, loading, error, refetch } = useFetch(url);

  const handleDelete = (row) => {
    confirmAlert({
      title: "",
      message: "Are you sure to delete this Register ?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(row),
        },
        {
          label: "No",
          //onClick: () => alert('Click No')
        },
      ],
    });
  };

  const deleteRecord = (row) => {
    let userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };
    axios
      .delete(
        `${process.env.REACT_APP_API_URL}/burial/delete/${row._id}`,
        config
      )
      .then((response) => {
        toast.success("Register Deleted Successfully.");
        refetch();
      })
      .catch((err) => {
        errorLogApi(err, "BurialChild", "/burial");
      });
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const filteredData = useMemo(() => {
    return (
      burials?.filter((burial) =>
        Object.values(burial).some((value) =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      ) || []
    );
  }, [burials, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="px-4 mx-auto w-full max-w-7xl sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Burial Register</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FaPlus />}
          onClick={() => navigate("/burial/add")}
        >
          Add Burial
        </Button>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search burials..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Record No.</TableCell>
              <TableCell>Date of Demise</TableCell>
              <TableCell>Deceased Name</TableCell>
              <TableCell>Profession</TableCell>
              <TableCell>Location of Burial</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row._id}>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    <Tooltip title="View details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          toggleModal();
                          setCurrentRecord(row);
                        }}
                      >
                        <FaEye className="text-blue-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate("/burial/update", { state: row })
                        }
                      >
                        <FaEdit className="text-gray-600" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row)}
                      >
                        <FaTrash className="text-red-600" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell>{row.record_no}</TableCell>
                <TableCell>
                  {dayjs(row.date_of_demise).utc().format("MM/DD/YYYY")}
                </TableCell>
                <TableCell>{row.deceased_name}</TableCell>
                <TableCell>{row.profession}</TableCell>
                <TableCell>{row.location_of_burial}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex justify-between items-center mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/burial/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <BurialView currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default BurialRegisterChild;
